import React, {
    useEffect,
    useState,
    useRef,
    useLayoutEffect,
    useCallback
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

const DESCENDER_PADDING_PX = 4;

function HeroV4(props) {
    const {
        titleList,
        titleSuffix,
        copy,
        display,
        logoInfo
    } = props;

    if (!display) return null;

    const { link, altText } = (logoInfo || {});

    const [pause, setPause] = useState(false);
    const [itemHeight, setItemHeight] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const listRef = useRef(null);
    const resizeHandleRef = useRef(null);

    const normalizedTitleList = Array.isArray(titleList) ? titleList : [];
    const titleCount = normalizedTitleList.length;
    const hasTitles = titleCount > 0;
    const firstTitle = hasTitles ? normalizedTitleList[0] : '';

    const measureItemHeight = useCallback(() => {
        if (!listRef.current) return;
        const nodes = Array.from(listRef.current.children || []);
        if (nodes.length === 0) return;
        const heights = nodes.map((node) => node.offsetHeight || 0);
        const max = Math.max(...heights);
        if (max > 0) {
            if (!isReady) {
                setIsReady(true);
            }
            if (max !== itemHeight) {
                setItemHeight(max);
            }
        }
    }, [itemHeight, isReady]);

    useLayoutEffect(() => {
        measureItemHeight();
    }, [measureItemHeight, titleList]);

    useEffect(() => {
        if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
            document.fonts.ready
                .then(() => {
                    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                        window.requestAnimationFrame(() => {
                            measureItemHeight();
                        });
                    } else {
                        measureItemHeight();
                    }
                })
                .catch((error) => {
                    if (process.env.NODE_ENV !== 'production') {
                        // eslint-disable-next-line no-console
                        console.warn('HeroV4: Font loading failed, text measurements may be inaccurate:', error);
                    }
                    setTimeout(() => {
                        measureItemHeight();
                    }, 500);
                });
        }
    }, [measureItemHeight]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleResize = () => {
            if (resizeHandleRef.current) {
                return;
            }

            if (typeof window.requestAnimationFrame === 'function') {
                const id = window.requestAnimationFrame(() => {
                    resizeHandleRef.current = null;
                    measureItemHeight();
                });
                resizeHandleRef.current = {
                    type: 'raf',
                    id
                };
            } else {
                const timeoutId = setTimeout(() => {
                    resizeHandleRef.current = null;
                    measureItemHeight();
                }, 60);
                resizeHandleRef.current = {
                    type: 'timeout',
                    id: timeoutId
                };
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            if (resizeHandleRef.current) {
                if (resizeHandleRef.current.type === 'raf' && typeof window.cancelAnimationFrame === 'function') {
                    window.cancelAnimationFrame(resizeHandleRef.current.id);
                } else if (resizeHandleRef.current.type === 'timeout') {
                    clearTimeout(resizeHandleRef.current.id);
                }
                resizeHandleRef.current = null;
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [measureItemHeight]);

    useEffect(() => {
        if (pause || titleCount <= 1) {
            return undefined;
        }

        const interval = setInterval(() => {
            setIsTransitioning(false);
            setCurrentIndex((prev) => {
                const nextIndex = prev + 1;
                if (nextIndex >= titleCount) {
                    return titleCount;
                }
                return nextIndex;
            });
        }, 5000);

        return () => {
            clearInterval(interval);
        };
    }, [pause, titleCount]);

    const transform = itemHeight ? `translateY(-${currentIndex * itemHeight}px)` : 'translateY(0px)';

    const listStyle = {
        height: itemHeight ? `${itemHeight}px` : 'auto',
        overflow: 'hidden',
        position: 'relative'
    };

    const handleTransitionEnd = useCallback(() => {
        if (currentIndex === titleCount) {
            setIsTransitioning(true);
            requestAnimationFrame(() => {
                setCurrentIndex(0);
            });
        }
    }, [currentIndex, titleCount]);

    useEffect(() => {
        if (isTransitioning && currentIndex === 0) {
            requestAnimationFrame(() => {
                setIsTransitioning(false);
            });
        }
    }, [currentIndex, isTransitioning]);

    const getTransition = () => {
        if (isTransitioning || pause) return 'none';
        return 'transform 1.2s ease-in-out';
    };

    const ulStyle = {
        transform,
        transition: getTransition(),
        margin: 0,
        padding: 0,
        listStyle: 'none',
        visibility: isReady ? 'visible' : 'hidden'
    };

    const handlePageflowNext = useCallback(() => {
        setPause(true);
    }, []);

    useEffect(() => {
        window.addEventListener('pageflow.next', handlePageflowNext);
        return () => window.removeEventListener('pageflow.next', handlePageflowNext);
    }, [handlePageflowNext]);

    const listItemStyle = {
        paddingBottom: `${DESCENDER_PADDING_PX}px`
    };

    return (
        <div className={cn('ulx__aem-section', 'ulx__aem-hero__content')}>
            <img src={link} alt={altText} width="auto" height="42" />
            <h1>
                {
                    titleCount === 1 && firstTitle
                }
                {
                    titleCount > 1 && (
                        <div
                            className={cn('rotating-list')}
                            style={listStyle}
                            role="marquee"
                        >
                            {
                                !isReady && hasTitles && (
                                    <span
                                        className={cn('rotating-list__placeholder')}
                                    >
                                        {firstTitle}
                                    </span>
                                )
                            }
                            <ul
                                ref={listRef}
                                style={ulStyle}
                                onTransitionEnd={handleTransitionEnd}
                            >
                                {
                                    // eslint-disable-next-line react/no-array-index-key
                                    normalizedTitleList.map((title, k) => (
                                        <li key={`title-item-${k}`} style={listItemStyle}>{title}</li> /* eslint-disable-line react/no-array-index-key */
                                    ))
                                }
                                {hasTitles && (
                                    <li style={listItemStyle}>{firstTitle}</li>
                                )}
                            </ul>
                        </div>
                    )
                }
                {titleSuffix}
            </h1>
            <p>{copy}</p>
        </div>
    );
}

HeroV4.propTypes = {
    logoInfo: PropTypes.shape({
        link: PropTypes.string,
        altText: PropTypes.string
    }).isRequired,
    copy: PropTypes.string.isRequired,
    display: PropTypes.bool.isRequired,
    titleList: PropTypes.arrayOf(PropTypes.string),
    titleSuffix: PropTypes.string.isRequired
};

HeroV4.defaultProps = {
    titleList: []
};

export default HeroV4;
